import { useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { fmt, downloadFile } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "./Dashboard";

export default function Reports() {
  const [campaigns, setCampaigns] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    api.get("/client/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
  }, []);

  const download = async (c, format) => {
    const key = `${c.id}-${format}`;
    setDownloading(key);
    try {
      await downloadFile(`/client/reports/campaign/${c.id}?format=${format}`, `${c.name.replace(/\s+/g, "_")}_report.${format}`);
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch {
      toast.error("Report generation failed");
    } finally {
      setDownloading("");
    }
  };

  if (!campaigns) return <Skeleton className="h-64 bg-white/5" />;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <p className="section-label">Reporting</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Campaign Reports</h2>
        <p className="text-sm text-slate-400 mt-2">Professional campaign reports with location performance, media cost breakdowns and executive summaries.</p>
      </div>

      {campaigns.length === 0 && (
        <div className="card-surface p-14 text-center">
          <FileText className="w-10 h-10 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">No campaigns available for reporting yet.</p>
        </div>
      )}

      <div className="space-y-5">
        {campaigns.map((c) => (
          <div key={c.id} className="card-surface p-6" data-testid={`report-card-${c.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display font-bold text-white text-lg">{c.name}</h3>
                  <StatusBadge status={c.status} />
                </div>
                <p className="text-sm text-slate-400 mt-1">{c.start_date} → {c.end_date} · {c.duration_days} days · {c.num_locations} locations · radius up to {fmt(c.max_radius)}m</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="border-white/10" disabled={!!downloading} onClick={() => download(c, "csv")} data-testid={`report-csv-${c.id}`}>
                  <Download className="w-4 h-4 mr-2" /> {downloading === `${c.id}-csv` ? "..." : "CSV / Excel"}
                </Button>
                <Button className="bg-blue-600 hover:bg-blue-500" disabled={!!downloading} onClick={() => download(c, "pdf")} data-testid={`report-pdf-${c.id}`}>
                  <Download className="w-4 h-4 mr-2" /> {downloading === `${c.id}-pdf` ? "Generating..." : "GENERATE REPORT"}
                </Button>
              </div>
            </div>
            <div className="mt-5 rounded-md border border-white/5 bg-secondary/20 p-5">
              <p className="text-[10px] tracking-widest text-slate-500 uppercase mb-3">Campaign Performance Summary</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                {[["Impressions", fmt(c.metrics?.impressions)], ["Reach", fmt(c.metrics?.reach)], ["Engagements", fmt(c.metrics?.engagements)],
                  ["Eng. Rate", `${c.metrics?.engagement_rate}%`], ["Clicks", fmt(c.metrics?.clicks)],
                  ["Media Cost", `R${fmt(c.budget)}`], ["Cost / Engagement", `R${(c.budget / Math.max(c.metrics?.engagements || 1, 1)).toFixed(2)}`]].map(([label, v]) => (
                  <div key={label}>
                    <p className="font-display font-bold text-white">{v}</p>
                    <p className="text-[10px] tracking-widest text-slate-500 uppercase mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
